import { z } from 'zod';
import { Point, Service, SourceSchema, StationSchema, type SimState } from './model';
import { stationFleet, canStationHost } from './organizations';
import { CatalogReviewSchema, PublishedFleetSchema, EvidenceUrl } from './catalogEvidence';
export const CatalogEntrySchema = z.object({
  id: z.string(),
  name: z.string().min(2).max(160),
  service: Service,
  organization: z.string().max(160),
  stationType: z.string().max(100),
  city: z.string().max(100),
  postcode: z.string().max(20),
  address: z.string().max(200),
  location: Point,
  source: SourceSchema,
  hvo: z.boolean(),
  website: EvidenceUrl.optional(),
  review: CatalogReviewSchema.optional(),
  fleet: PublishedFleetSchema.optional(),
});
export type CatalogEntry = z.infer<typeof CatalogEntrySchema>;
export type CatalogMetadata = {
  version: 1;
  fetchedAt: string;
  dataTimestamp: string;
  source: string;
  license: string;
  attribution: string;
  count: number;
  counts: Record<string, number>;
  hvoCount: number;
  rawCount: number;
  duplicatesRemoved: number;
  queryFile: string;
  revision?: string;
  review?: {
    checkedAt: string;
    reviewed: number;
    named: number;
    publishedFleets: number;
    publishedVehicles: number;
    publishedCallSigns: number;
    unverifiedFleets: number;
    excludedLocations: number;
    byState: Record<string, number>;
  };
};
export type CatalogResult = {
  metadata: CatalogMetadata | null;
  total: number;
  offset: number;
  entries: CatalogEntry[];
};
const sameSource = (entry: CatalogEntry, station: SimState['stations'][number]) =>
  Boolean(
    entry.source.id &&
      station.source?.id === entry.source.id &&
      station.source.kind === entry.source.kind,
  );

export function autoEquippable(entry: CatalogEntry) {
  return !entry.review || entry.review.operational === 'listed';
}
export function isGeneratedCatalogVehicle(v: SimState['vehicles'][number]) {
  return (
    v.catalogManaged === true ||
    (v.catalogManaged === undefined &&
      v.simulated === true &&
      v.id.startsWith('v-catalog-') &&
      v.name.startsWith('SIM '))
  );
}
function retirePlaceholders(s: SimState, stationId: string) {
  s.vehicles = s.vehicles.filter((v) => {
    if (v.stationId !== stationId || !isGeneratedCatalogVehicle(v) || v.evidence) return true;
    // Older saves lack ownership metadata. Preserve purchased specialty extensions.
    const station = s.stations.find((w) => w.id === stationId);
    if (
      v.catalogManaged === undefined &&
      station &&
      !stationFleet[station.service].includes(v.kind) &&
      v.kind !== 'HvO'
    )
      return true;
    if (v.incidentId || v.journey) {
      v.catalogRetire = true;
      return true;
    }
    return false;
  });
}

export function activateEntries(
  state: SimState,
  entries: CatalogEntry[],
  equip: boolean,
  areaManaged = false,
) {
  const s = structuredClone(state);
  const unique = [...new Map(entries.map((e) => [e.id, CatalogEntrySchema.parse(e)])).values()];
  const newEntries = unique.filter(
    (e) => autoEquippable(e) && !s.stations.some((w) => sameSource(e, w)),
  );
  if (s.stations.length + newEntries.length > 1200)
    throw Error('Maximal 1.200 aktive Wachen. Weniger Standorte auswählen.');
  for (const entry of unique) {
    let w = s.stations.find((w) => sameSource(entry, w));
    // Pre-evidence saves had no edit flag. A non-catalogue name signals a player edit.
    if (
      w &&
      !w.review &&
      w.catalogCustomized === undefined &&
      entry.review &&
      w.name !== entry.review.originalName &&
      w.name !== entry.name
    )
      w.catalogCustomized = true;
    if (!autoEquippable(entry)) {
      if (w) {
        w.review = entry.review;
        retirePlaceholders(s, w.id);
      }
      continue;
    }
    if (!w) {
      w = StationSchema.parse({
        id: `catalog-${entry.id}`,
        name: entry.name,
        service: entry.service,
        location: entry.location,
        organization: entry.organization,
        stationType: entry.stationType,
        source: entry.source,
        areaManaged: areaManaged || undefined,
        review: entry.review,
        fleet: entry.fleet,
      });
      s.stations.push(w);
    } else if (
      !w.catalogCustomized &&
      areaManaged &&
      s.vehicles
        .filter((v) => v.stationId === w!.id)
        .every((v) => canStationHost(entry.service, v.kind))
    ) {
      Object.assign(w, {
        name: entry.name,
        service: entry.service,
        location: [...entry.location],
        organization: entry.organization,
        stationType: entry.stationType,
        source: entry.source,
        review: entry.review,
        fleet: entry.fleet,
      });
      for (const v of s.vehicles.filter(
        (v) => v.stationId === w!.id && !v.incidentId && !v.journey,
      )) {
        v.location = [...entry.location];
        if (isGeneratedCatalogVehicle(v) && !v.evidence) {
          const suffix = v.name.match(/ ([^ ]+) (\d+-\d+)$/)?.[0] ?? ` ${v.kind}`;
          v.name = `SIM ${w.name.slice(0, 95 - suffix.length)}${suffix}`;
        }
      }
    }
    if (equip) retirePlaceholders(s, w.id);
    if (!equip || w.catalogCustomized) continue;
    if (entry.fleet && !w.catalogCustomized) {
      w.fleet = entry.fleet;
      w.review = entry.review;
      retirePlaceholders(s, w.id);
      const existingIds = new Set(
        entry.fleet.vehicles
          .filter(
            (v) => v.availability === 'published' && !v.unitOnly && v.kind && canStationHost(w!.service, v.kind),
          )
          .map((v) => v.id),
      );
      s.vehicles = s.vehicles.filter((v) => {
        if (
          v.stationId !== w!.id ||
          !v.catalogManaged ||
          !v.evidence ||
          existingIds.has(v.evidence.id)
        )
          return true;
        if (v.incidentId || v.journey) {
          v.catalogRetire = true;
          return true;
        }
        return false;
      });
      for (const fact of entry.fleet.vehicles) {
        if (
          !fact.kind ||
          fact.availability !== 'published' ||
          fact.unitOnly ||
          !canStationHost(w.service, fact.kind)
        )
          continue;
        const id = `published-${entry.id}-${fact.id}`;
        const current = s.vehicles.find((v) => v.id === id);
        const ordinal =
          entry.fleet.vehicles
            .filter((v) => v.typeLabel === fact.typeLabel)
            .findIndex((v) => v.id === fact.id) + 1;
        const name = (
          fact.callSign ??
          `${fact.typeLabel}${fact.radioIdentifier ? ' [' + fact.radioIdentifier + ']' : ''} · ${entry.review?.municipality ?? entry.city} · Einheit ${ordinal}`
        ).slice(0, 100);
        if (current) {
          if (current.catalogManaged && !current.incidentId && !current.journey)
            Object.assign(current, {
              name,
              kind: fact.kind,
              simulated: false,
              evidence: fact,
              catalogRetire: false,
            });
          continue;
        }
        if (s.vehicles.length >= 4000) throw Error('Maximal 4.000 aktive Fahrzeuge.');
        s.vehicles.push({
          id,
          name,
          kind: fact.kind,
          simulated: false,
          catalogManaged: true,
          catalogRetire: false,
          evidence: fact,
          stationId: w.id,
          status: 2,
          incidentId: null,
          journey: null,
          location: [...w.location],
        });
      }
      // A published fleet is authoritative for automatic equipment. Never add a generic DLK/NEF.
      continue;
    }
    // Unknown fleets stay empty. Player purchases remain explicitly simulated.

  }
  s.revision = Math.max(s.revision, state.revision) + 1;
  return s;
}

export function removeInactiveManagedAreaResources(
  state: SimState,
  keep: (station: SimState['stations'][number]) => boolean = () => false,
) {
  const s = structuredClone(state);
  const removable = new Set(
    s.stations
      .filter(
        (station) =>
          station.areaManaged &&
          !keep(station) &&
          !s.vehicles.some(
            (vehicle) =>
              vehicle.stationId === station.id && (vehicle.incidentId || vehicle.journey),
          ),
      )
      .map((station) => station.id),
  );
  if (!removable.size) return s;
  s.vehicles = s.vehicles.filter((vehicle) => !removable.has(vehicle.stationId));
  s.stations = s.stations.filter((station) => !removable.has(station.id));
  s.revision++;
  return s;
}
export { stationFleet };
